import React, { Fragment, useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import { WEBSERVICE, SITENAMEALIAS } from "../utils/init";
import { showToast } from "../utils/library";
import { getAuthToken, getAuthUuid } from "../utils/service";

const LIMIT = 10;

// ─────────────────────────────────────────────────────────────────
// CSS
// ─────────────────────────────────────────────────────────────────
const CSS = `
.ecd2-page { padding: 32px 24px 80px; max-width: 100%; overflow-x: hidden; }
.ecd2-hdr { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 24px; gap: 16px; flex-wrap: wrap; }
.ecd2-hdr-left { display: flex; flex-direction: column; gap: 4px; }
.ecd2-title { font-size: 22px; font-weight: 700; letter-spacing: -.4px; color: var(--text-color); margin: 0; }
.ecd2-sub { font-size: 13px; color: var(--text-color); opacity: .65; margin: 0; }

.ecd2-topbar { display: flex; gap: 10px; align-items: center; margin-bottom: 16px; flex-wrap: wrap; }
.ecd2-search-wrap { flex: 1; min-width: 180px; position: relative; }
.ecd2-search-icon { position: absolute; left: 11px; top: 50%; transform: translateY(-50%); color: var(--text-color); opacity: .5; pointer-events: none; }
.ecd2-search-input { width: 100%; height: 36px; padding: 0 12px 0 34px; background: var(--body-color-less); border: 1px solid var(--body-color-less2); border-radius: 8px; color: var(--text-color); font-family: inherit; font-size: 13px; outline: none; transition: border-color .2s; }
.ecd2-search-input::placeholder { color: var(--text-color); opacity: .5; }
.ecd2-search-input:focus { border-color: var(--btn-color); box-shadow: 0 0 0 3px color-mix(in srgb, var(--btn-color) 12%, transparent); }
.ecd2-filter-sel { height: 36px; padding: 0 12px; background: var(--body-color-less); border: 1px solid var(--body-color-less2); border-radius: 8px; color: var(--text-color); font-family: inherit; font-size: 13px; appearance: auto; cursor: pointer; outline: none; }
.ecd2-filter-sel:focus { border-color: var(--btn-color); }

.ecd2-tbl-wrap { background: var(--body-color-less); border: 1px solid var(--body-color-less2); border-radius: 12px; overflow-x: auto; -webkit-overflow-scrolling: touch; }
.ecd2-tbl-wrap table { width: 100%; border-collapse: collapse; min-width: 700px; }
.ecd2-tbl-wrap thead tr { border-bottom: 1px solid var(--body-color-less2); }
.ecd2-tbl-wrap thead th { padding: 12px 16px; font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: .65px; color: var(--text-color); opacity: .7; text-align: left; white-space: nowrap; }
.ecd2-tbl-wrap tbody tr { border-bottom: 1px solid var(--body-color-less2); transition: background .15s; }
.ecd2-tbl-wrap tbody tr:last-child { border-bottom: none; }
.ecd2-tbl-wrap tbody tr:hover { background: color-mix(in srgb, var(--btn-color) 4%, transparent); }
.ecd2-tbl-wrap tbody td { padding: 13px 16px; font-size: 13px; vertical-align: middle; }
.ecd2-td-main { font-weight: 600; color: var(--text-color); }
.ecd2-td-sub { font-size: 11px; color: var(--text-color); opacity: .6; margin-top: 2px; }
.ecd2-td-amount { font-weight: 700; color: var(--text-color); font-size: 14px; }

/* Dispute status badges */
.ecd2-badge { display: inline-flex; align-items: center; gap: 4px; padding: 3px 9px; border-radius: 20px; font-size: 11px; font-weight: 600; }
.ecd2-badge::before { content: ''; width: 5px; height: 5px; border-radius: 50%; flex-shrink: 0; }
.ecd2-badge.open     { background: rgba(217, 119, 6, .13); color: #d97706; }
.ecd2-badge.open::before     { background: #d97706; }
.ecd2-badge.closed   { background: rgba(16, 185, 129, .15); color: #10b981; }
.ecd2-badge.closed::before   { background: #10b981; }
.ecd2-badge.resolved { background: rgba(37, 99, 235, .12); color: #60a5fa; }
.ecd2-badge.resolved::before { background: #60a5fa; }
.ecd2-badge.pending  { background: rgba(139, 92, 246, .12); color: #a78bfa; }
.ecd2-badge.pending::before  { background: #a78bfa; }
.ecd2-badge.rejected { background: rgba(239, 68, 68, .1); color: #f87171; }
.ecd2-badge.rejected::before { background: #f87171; }

.ecd2-empty { display: flex; flex-direction: column; align-items: center; padding: 60px 20px; gap: 14px; text-align: center; }
.ecd2-empty-icon { font-size: 36px; opacity: .3; }
.ecd2-empty-text { font-size: 14px; color: var(--text-color); opacity: .6; }

/* Loading skeleton */
.ecd2-skeleton-row td { padding: 13px 16px; }
.ecd2-skeleton-line { height: 12px; border-radius: 6px; background: linear-gradient(90deg, var(--body-color-less2) 25%, color-mix(in srgb, var(--body-color-less2) 60%, transparent) 50%, var(--body-color-less2) 75%); background-size: 200% 100%; animation: ecd2-shimmer 1.4s infinite; }
.ecd2-skeleton-line.short { width: 60%; }
.ecd2-skeleton-line.xshort { width: 40%; margin-top: 5px; }
@keyframes ecd2-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

/* Error */
.ecd2-error { display: flex; flex-direction: column; align-items: center; padding: 40px 20px; gap: 10px; text-align: center; }
.ecd2-error-icon { font-size: 28px; color: #f87171; opacity: .7; }
.ecd2-error-text { font-size: 13px; color: #f87171; }
.ecd2-retry-btn { margin-top: 4px; height: 32px; padding: 0 16px; border-radius: 7px; border: 1px solid rgba(239,68,68,.3); background: rgba(239,68,68,.08); color: #f87171; font-family: inherit; font-size: 12px; cursor: pointer; transition: all .15s; }
.ecd2-retry-btn:hover { background: rgba(239,68,68,.16); }

/* Pagination */
.ecd2-pagination { display: flex; align-items: center; justify-content: flex-end; gap: 6px; margin-top: 16px; flex-wrap: wrap; }
.ecd2-page-btn { height: 34px; min-width: 34px; padding: 0 12px; border-radius: 7px; border: 1px solid var(--body-color-less2); background: var(--body-color-less); color: var(--text-color); font-family: inherit; font-size: 13px; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; gap: 6px; transition: all .15s; white-space: nowrap; }
.ecd2-page-btn:hover:not(:disabled) { border-color: var(--btn-color); color: var(--btn-color); }
.ecd2-page-btn:disabled { opacity: .38; cursor: not-allowed; }
.ecd2-page-btn.active { background: var(--btn-color); border-color: var(--btn-color); color: #fff; font-weight: 600; }
.ecd2-page-info { font-size: 13px; color: var(--text-color); opacity: .6; padding: 0 4px; }

.ecd2-action-btn { height: 30px; padding: 0 12px; font-size: 12px; border-radius: 6px; font-family: inherit; cursor: pointer; display: inline-flex; align-items: center; gap: 5px; transition: all .15s; white-space: nowrap; background: color-mix(in srgb, var(--btn-color) 12%, transparent); border: 1px solid color-mix(in srgb, var(--btn-color) 25%, transparent); color: var(--btn-color); }
.ecd2-action-btn:hover { background: color-mix(in srgb, var(--btn-color) 22%, transparent); }

@keyframes ecd2-fade-up { from { opacity: 0; transform: translateY(7px); } to { opacity: 1; transform: translateY(0); } }
.ecd2-view-in { animation: ecd2-fade-up .2s ease; }

@media (max-width: 700px) {
  .ecd2-page { padding: 16px 10px 80px; }
  .ecd2-topbar { flex-direction: column; }
  .ecd2-hdr { flex-direction: column; }
  .ecd2-pagination { justify-content: center; }
}
`;

// ─────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────
const DISPUTE_STATUS_LABELS = {
  OPEN:     "Open",
  CLOSED:   "Closed",
  RESOLVED: "Resolved",
  PENDING:  "Pending",
  REJECTED: "Rejected",
};

function DisputeStatusBadge({ status }) {
  const css = (status || "").toLowerCase();
  return (
    <span className={`ecd2-badge ${css}`}>
      {DISPUTE_STATUS_LABELS[status] || status || "—"}
    </span>
  );
}

function fmtDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });
}

// ─────────────────────────────────────────────────────────────────
// Skeleton
// ─────────────────────────────────────────────────────────────────
function SkeletonRows() {
  return Array.from({ length: 5 }).map((_, i) => (
    <tr key={i} className="ecd2-skeleton-row">
      <td><div className="ecd2-skeleton-line short" /></td>
      <td><div className="ecd2-skeleton-line short" /></td>
      <td><div className="ecd2-skeleton-line short" /></td>
      <td><div className="ecd2-skeleton-line short" /></td>
      <td><div className="ecd2-skeleton-line short" /></td>
      <td><div className="ecd2-skeleton-line short" /></td>
      <td><div className="ecd2-skeleton-line short" /></td>
    </tr>
  ));
}

// ─────────────────────────────────────────────────────────────────
// Pagination
// ─────────────────────────────────────────────────────────────────
function Pagination({ currentPage, totalRecords, loading, onPageChange }) {
  const totalPages = Math.ceil(totalRecords / LIMIT) || 1;
  const hasMore    = currentPage < totalPages;
  return (
    <div className="ecd2-pagination">
      <button
        className="ecd2-page-btn"
        disabled={currentPage === 1 || loading}
        onClick={() => onPageChange(currentPage - 1)}
      >
        <i className="fas fa-chevron-left" style={{ fontSize: 11 }} /> Prev
      </button>
      <span className="ecd2-page-info">Page {currentPage} of {totalPages}</span>
      <button
        className="ecd2-page-btn"
        disabled={!hasMore || loading}
        onClick={() => onPageChange(currentPage + 1)}
      >
        Next <i className="fas fa-chevron-right" style={{ fontSize: 11 }} />
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────
export default function EscrowContractDispute() {
  const history = useHistory();

  const merchantId = localStorage.getItem(SITENAMEALIAS + "_merchant_id");

  const [search, setSearch]                   = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [disputes, setDisputes]               = useState([]);
  const [loading, setLoading]                 = useState(false);
  const [error, setError]                     = useState(null);
  const [currentPage, setCurrentPage]         = useState(1);
  const [totalRecords, setTotalRecords]       = useState(0);
  const [refreshKey, setRefreshKey]           = useState(0);

  const debounceTimer = useRef(null);

  // ── Debounce search ────────────────────────────────────────────
  useEffect(() => {
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(debounceTimer.current);
  }, [search]);

  // ── Fetch disputes ─────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const offset = (currentPage - 1) * LIMIT;
    const params = new URLSearchParams({ offset, limit: LIMIT, merchantId });
    if (debouncedSearch) params.append("search", debouncedSearch);

    fetch(`${WEBSERVICE}/escrow/get-escrow-disputes-by-contract?${params.toString()}`, {
      method: "GET",
      headers: {
        Authorization: "bearer " + getAuthToken(),
        UUID: getAuthUuid(),
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        if (data.status === false) {
          showToast("error", data.message || "Something went wrong.");
          setDisputes([]);
          setTotalRecords(0);
          setLoading(false);
          return;
        }
        const raw  = Array.isArray(data.data?.disputes) ? data.data.disputes : [];
        // Deduplicate by disputeId (API may return same dispute multiple times for each participant)
        const seen = new Set();
        const list = raw.filter((d) => {
          if (seen.has(d.disputeId)) return false;
          seen.add(d.disputeId);
          return true;
        });
        setDisputes(list);
        setTotalRecords(data.data?.totalRecords ?? 0);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Failed to load disputes. Please try again.");
        setDisputes([]);
        setTotalRecords(0);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [currentPage, debouncedSearch, refreshKey]);

  const colSpan = 7;

  // ─────────────────────────────────────────────────────────────
  return (
    <Fragment>
      <style>{CSS}</style>
      <div className="container-scroller">
        <Sidebar />
        <Header />
        <div className="main-panel">
          <div className="ecd2-page ecd2-view-in">

            {/* Header */}
            <div className="ecd2-hdr">
              <div className="ecd2-hdr-left">
                <h1 className="ecd2-title">Escrow Disputes</h1>
                <p className="ecd2-sub">View and track all disputes raised on escrow contracts</p>
              </div>
            </div>

            {/* Top bar */}
            <div className="ecd2-topbar">
              <div className="ecd2-search-wrap">
                <span className="ecd2-search-icon">
                  <i className="fas fa-search" style={{ fontSize: 13 }} />
                </span>
                <input
                  className="ecd2-search-input"
                  placeholder="Search by contract ID or description…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            {/* Table */}
            <div className="ecd2-tbl-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Escrow Number</th>
                    <th>Disputing Party</th>
                    <th>Escrow Contract Value</th>
                    <th>Dispute Status</th>
                    <th>Files</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <SkeletonRows />
                  ) : error ? (
                    <tr>
                      <td colSpan={colSpan}>
                        <div className="ecd2-error">
                          <div className="ecd2-error-icon">
                            <i className="fas fa-exclamation-circle" />
                          </div>
                          <p className="ecd2-error-text">{error}</p>
                          <button
                            className="ecd2-retry-btn"
                            onClick={() => setRefreshKey((k) => k + 1)}
                          >
                            <i className="fas fa-redo" style={{ fontSize: 11, marginRight: 5 }} />
                            Retry
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : disputes.length === 0 ? (
                    <tr>
                      <td colSpan={colSpan}>
                        <div className="ecd2-empty">
                          <div className="ecd2-empty-icon">
                            <i className="fas fa-gavel" />
                          </div>
                          <p className="ecd2-empty-text">No disputes found</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    disputes.map((d) => (
                      <tr key={d.disputeId}>
                        {/* Date */}
                        <td style={{ color: "var(--text-color)", opacity: 0.75 }}>
                          {fmtDate(d.createdAt)}
                        </td>

                        {/* Escrow Number */}
                        <td>
                          <div className="ecd2-td-main">{d.escrowNo || `#${d.contractId}`}</div>
                          {d.milestoneName && (
                            <div className="ecd2-td-sub">{d.milestoneName}</div>
                          )}
                        </td>

                        {/* Disputing Party */}
                        <td>
                          {d.payerEmail && (
                            <div className="ecd2-td-main">{d.payerEmail}</div>
                          )}
                          {d.payeeEmail && (
                            <div className="ecd2-td-sub">{d.payeeEmail}</div>
                          )}
                          {!d.payerEmail && !d.payeeEmail && (
                            <div className="ecd2-td-main">—</div>
                          )}
                        </td>

                        {/* Escrow Contract Value */}
                        <td>
                          <div className="ecd2-td-amount">
                            {d.contractAmount != null ? `${d.fundedCurrency ? d.fundedCurrency + " " : ""}${Number(d.contractAmount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}
                          </div>
                        </td>

                        {/* Dispute Status */}
                        <td>
                          <DisputeStatusBadge status={d.status} />
                        </td>

                        {/* Files */}
                        <td>
                          {d.filename ? (
                            <a
                              href={d.fileurl || d.filename}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                display: "inline-flex", alignItems: "center", gap: 5,
                                fontSize: 12, fontWeight: 500, color: "var(--btn-color)",
                                textDecoration: "none",
                              }}
                            >
                              <i className="fas fa-paperclip" style={{ fontSize: 11 }} />
                              {d.filename.split("/").pop()}
                              <i className="fas fa-external-link-alt" style={{ fontSize: 9, opacity: 0.7 }} />
                            </a>
                          ) : (
                            <span style={{ color: "var(--text-color)", opacity: 0.4, fontSize: 13 }}>—</span>
                          )}
                        </td>

                        {/* Actions */}
                        <td>
                          <button
                            className="ecd2-action-btn"
                            onClick={() => history.push(`/escrow/payer/contract/${d.escrowNo}`)}
                          >
                            <i className="fas fa-eye" style={{ fontSize: 11 }} /> View
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {!error && (
              <Pagination
                currentPage={currentPage}
                totalRecords={totalRecords}
                loading={loading}
                onPageChange={setCurrentPage}
              />
            )}

          </div>
          <Footer />
        </div>
      </div>
    </Fragment>
  );
}
